// src/cockpit4/workspaces/SettingsWorkspace.tsx
// ============================================
// Settings Workspace - 系统设置页面
//
// 内容：
//   1. API Key 热插拔配置（多米/DeepSeek/微信/Suno）
//   2. 计划存档查看器（按日/周/月时间分类）
//   3. 环境变量状态
//   4. Runtime 列表
//   5. 知识库路径
//   6. 关于信息
// ============================================

import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Settings,
  KeyRound,
  Boxes,
  FolderTree,
  Info,
  CheckCircle2,
  XCircle,
  Archive,
  Calendar,
  Loader2,
  RefreshCw,
  Eye,
  EyeOff,
  Save,
  TestTube,
  RotateCcw,
  Send,
  FileText,
  Shield,
  Cpu,
  // P2 图标
  Building2,
  ShieldCheck,
  Activity,
  // P1 集成开放（API 平台 / 第三方集成 / Webhook）
  PlugZap,
  Webhook,
  // v6：AI 自主赚钱 SOP
  DollarSign,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { useCockpitData } from "../../hooks/useCockpitData";
import { useUserStore } from "../../stores/userStore";
import { apiConfig, type ApiConfig } from "../../services/apiConfig";
import { getWeChatPublishService } from "../../services/wechatPublish";
import AccountSettings from "../components/AccountSettings";
import AuditLogPanel from "../components/AuditLogPanel";
// v6.22 登录审计面板（管理员专属：员工登录记录/IP/归属地/次数，读 Vault LoginLog）
import LoginAuditPanel from "../components/LoginAuditPanel";
// Runtime 收敛治理看板（52 Runtime → 目标 6 层：保留/并入/移出）
import RuntimeGovernancePanel from "../components/RuntimeGovernancePanel";
import AuditLogEnhanced from "../components/AuditLogEnhanced";
import RBACPanel from "../components/RBACPanel";
import BackupPanel from "../components/BackupPanel";
import EnterpriseBrainPanel from "../components/EnterpriseBrainPanel";
// P1-功能4：API 开放平台（深度扩展）
import APIPlatformPanel from "../components/APIPlatformPanel";
// P1-功能5：第三方集成市场（深度扩展）
import IntegrationMarketPanel from "../components/IntegrationMarketPanel";
// P1-功能6：Webhook 与事件系统（深度扩展）
import WebhookConsolePanel from "../components/WebhookConsolePanel";
// P2-功能7：多租户与私有化
import MultiTenantPanel from "../components/MultiTenantPanel";
// v6：AI 自主赚钱 SOP（系统如何从多维度实施赚钱的操作说明）
import AIMoneySOP from "../components/AIMoneySOP";
// P2-功能8：安全加固
import SecurityHardening from "../components/SecurityHardening";
// P2-功能9：运维监控
import OpsMonitoring from "../components/OpsMonitoring";
// Token 消耗记录面板（用户需求 #6）
import TokenUsagePanel from "../components/TokenUsagePanel";
import { vaultConfig } from "../../runtime/shared/vaultConfig";
import { UserCircle } from "lucide-react";

// ============================================
// 类型
// ============================================

interface EnvStatus {
  key: string;
  label: string;
  present: boolean;
  source: "import.meta.env" | "process.env";
}

interface RuntimeInfo {
  name: RuntimeName;
  status: string;
  ready: boolean;
}

interface VaultFileEntry {
  name: string;
  path: string;
  mtime?: string;
  size?: number;
}

type ArchiveTab = "daily" | "weekly" | "monthly";

// ============================================
// 主组件
// ============================================

export default function SettingsWorkspace() {
  const cockpitData = useCockpitData();
  // v6.22 登录审计仅超级管理员可见（员工不显示审计区）；Settings 敏感区（API Key/权限/审计等）仅管理员
  const currentUser = useUserStore((s) => s.currentUser);
  const isAdmin = currentUser?.role === "super_admin";
  const [runtimes, setRuntimes] = useState<RuntimeInfo[]>([]);

  // API 配置
  const [apiConfigs, setApiConfigs] = useState<ApiConfig[]>([]);
  const [editingConfig, setEditingConfig] = useState<string | null>(null);
  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<Record<string, { success: boolean; message: string; latency?: number }>>({});

  // 计划存档
  const [activeArchiveTab, setActiveArchiveTab] = useState<ArchiveTab>("daily");
  const [archiveFiles, setArchiveFiles] = useState<VaultFileEntry[]>([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [previewContent, setPreviewContent] = useState<{ title: string; content: string; loading: boolean } | null>(null);

  // ============================================
  // Runtime 状态刷新
  // ============================================
  useEffect(() => {
    const refresh = () => {
      try {
        const summary = RuntimeManager.getStatusSummary();
        const list: RuntimeInfo[] = cockpitData.runtimes.map((r) => ({
          name: r.name,
          status: r.status,
          ready: r.status === "ready",
        }));
        if (list.length === 0) {
          const all = RuntimeManager.getAllRuntimes();
          setRuntimes(
            all.map((name) => ({
              name,
              status: summary.status[name] || "unknown",
              ready: summary.status[name] === "ready",
            }))
          );
          return;
        }
        setRuntimes(list);
      } catch (err) {
        console.warn("[SettingsWorkspace] 拉取 Runtime 状态失败", err);
      }
    };
    refresh();
    const timer = setInterval(refresh, 5000);
    return () => clearInterval(timer);
  }, [cockpitData.runtimes]);

  // ============================================
  // API 配置加载
  // ============================================
  const refreshApiConfigs = useCallback(() => {
    setApiConfigs(apiConfig.getAll());
  }, []);

  useEffect(() => {
    refreshApiConfigs();
    const unsub = apiConfig.subscribe(refreshApiConfigs);
    return unsub;
  }, [refreshApiConfigs]);

  // ============================================
  // 计划存档加载
  // ============================================
  const loadArchive = useCallback(async (tab: ArchiveTab) => {
    setArchiveLoading(true);
    setArchiveFiles([]);
    try {
      const dirMap: Record<ArchiveTab, string> = {
        daily: "10_Operations（运营层）/Daily",
        weekly: "10_Operations（运营层）/Weekly",
        monthly: "10_Operations（运营层）/Monthly",
      };
      const dir = dirMap[tab];
      const resp = await fetch(`/vault-api/list?dir=${encodeURIComponent(dir)}&ext=.md`);
      if (resp.ok) {
        const result = await resp.json();
        if (result.success) {
          const files: VaultFileEntry[] = result.files || [];
          files.sort((a: VaultFileEntry, b: VaultFileEntry) => (b.mtime || "").localeCompare(a.mtime || ""));
          setArchiveFiles(files);
        }
      }
    } catch (err) {
      console.warn("[SettingsWorkspace] 加载存档失败", err);
    } finally {
      setArchiveLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArchive(activeArchiveTab);
  }, [activeArchiveTab, loadArchive]);

  /** 查看存档文件内容 */
  const openArchiveFile = async (file: VaultFileEntry) => {
    setPreviewContent({ title: file.name.replace(/\.md$/i, ""), content: "", loading: true });
    try {
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(file.path)}`);
      const result = await resp.json();
      setPreviewContent({
        title: file.name.replace(/\.md$/i, ""),
        content: result.content || "暂无内容",
        loading: false,
      });
    } catch (err: any) {
      setPreviewContent({ title: file.name, content: `加载失败：${err?.message}`, loading: false });
    }
  };

  // ============================================
  // API 配置操作
  // ============================================
  // P12 短板4/5/8：微信 AppSecret 安全托管 - 保存时同步到后端 /api/wechat/config（Vault 敏感目录），
  // 前端不落密钥，token 获取由后端代理代填
  const handleSaveConfig = async (id: string, updates: { apiKey?: string; apiBase?: string; extra?: Record<string, string> }) => {
    if (id === "wechat") {
      const appId = updates.extra?.appId?.trim() || "";
      const appSecret = updates.extra?.appSecret || "";
      // 本地仅存 appId，绝不落 appSecret
      apiConfig.update(id, { ...updates, extra: { ...updates.extra, appId } });
      setEditingConfig(null);
      try {
        const resp = await fetch("/api/wechat/config", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ appId, appSecret }),
        });
        const data = await resp.json();
        if (resp.ok && data?.success) {
          apiConfig.update(id, { extra: { appId, _secretStored: "yes" } });
        } else {
          console.error("[wechat-config] 保存失败", data?.error || resp.status);
        }
      } catch (e) {
        console.error("[wechat-config] 网络错误", e);
      }
      return;
    }
    apiConfig.update(id, updates);
    setEditingConfig(null);
  };

  const handleToggleEnabled = (id: string, enabled: boolean) => {
    apiConfig.update(id, { enabled });
  };

  const handleTest = async (id: string) => {
    setTestingId(id);
    const result = await apiConfig.test(id);
    setTestResult({ ...testResult, [id]: result });
    setTestingId(null);
  };

  const handleReset = (id: string) => {
    apiConfig.reset(id);
    setTestResult({ ...testResult, [id]: { success: false, message: "已重置" } });
  };

  // 测试微信公众号配置
  const handleTestWeChat = async () => {
    setTestingId("wechat");
    const service = getWeChatPublishService();
    const token = await service.getAccessToken();
    const result = token
      ? { success: true, message: "微信公众号连接成功，access_token 已获取" }
      : { success: false, message: "连接失败，请检查 AppID 和 AppSecret" };
    setTestResult({ ...testResult, wechat: result });
    setTestingId(null);
  };

  // 环境变量检测
  const envStatus: EnvStatus[] = [
    { key: "DEEPSEEK_API_KEY", label: "DeepSeek API Key", present: checkEnv("VITE_DEEPSEEK_API_KEY", "DEEPSEEK_API_KEY"), source: "import.meta.env" },
    { key: "OPENAI_API_KEY", label: "OpenAI API Key", present: checkEnv("VITE_OPENAI_API_KEY", "OPENAI_API_KEY"), source: "import.meta.env" },
    { key: "FEISHU_APP_ID", label: "飞书 App ID", present: checkEnv("VITE_FEISHU_APP_ID", "FEISHU_APP_ID"), source: "import.meta.env" },
    { key: "DOUYIN_APP_ID", label: "抖音 App ID", present: checkEnv("VITE_DOUYIN_APP_ID", "DOUYIN_APP_ID"), source: "import.meta.env" },
  ];

  return (
    <div className="h-full w-full overflow-hidden p-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-md bg-gray-500/15 border border-gray-500/30 flex items-center justify-center">
              <Settings size={14} className="text-gray-300" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white tracking-tight">设置</h2>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                API 热插拔 · 计划存档 · 环境变量 · 运行时
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {/* ============ v6.1 分类快捷索引条（19 个设置项按 5 组分类，点击滚动定位） ============ */}
          <div className="sticky top-0 z-10 mb-3 flex items-center gap-1 flex-wrap rounded-lg border border-white/[0.06] bg-white/[0.03] backdrop-blur-xl px-2 py-1.5">
            {([
              { id: "sec-account", label: "账户与安全", icon: Shield, color: "#8B5CF6", n: 5 },
              { id: "sec-ai", label: "AI 与赚钱", icon: DollarSign, color: "#34D399", n: 3 },
              { id: "sec-brain", label: "大脑与数据", icon: FolderTree, color: "#10B981", n: 4 },
              { id: "sec-dev", label: "集成与开发者", icon: PlugZap, color: "#38BDF8", n: 4 },
              { id: "sec-ops", label: "运维与信息", icon: Activity, color: "#F59E0B", n: 3 },
            ]).map((g) => {
              const GIcon = g.icon;
              return (
                <button
                  key={g.id}
                  onClick={() => {
                    const el = document.getElementById(g.id);
                    el?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-mono uppercase tracking-wider text-gray-400 hover:text-gray-200 hover:bg-white/[0.05] border border-transparent hover:border-white/[0.08] transition-colors"
                  title={`跳转到「${g.label}」（${g.n} 项）`}
                >
                  <GIcon size={10} style={{ color: g.color }} />
                  {g.label}
                  <span className="text-[8px] text-gray-600">{g.n}</span>
                </button>
              );
            })}
          </div>

          {/* ===== 组 1/5：账户与安全 ===== */}
          <div id="sec-account" className="scroll-mt-2 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-purple-400/90">🛡 账户与安全</span>
              <div className="h-px flex-1 bg-white/[0.05]" />
              <span className="text-[9px] font-mono text-gray-600">5 项</span>
            </div>
            <div className="space-y-3">
          {/* ============ 账户设置（修改密码 / 员工管理 / API 余额） ============ */}
          <Section title="账户设置" subtitle="Account · 修改密码 · 员工管理 · API 余额" icon={UserCircle} color="#8B5CF6">
            <AccountSettings />
          </Section>

          {/* ============ 企业审计日志（白皮书第六章⑩：操作日志留痕，仅管理员） ============ */}
          {isAdmin && (
            <Section title="企业审计日志" subtitle="Audit Log · 登录/登出/注册 真实记录" icon={Shield} color="#10B981">
              <AuditLogPanel days={7} compact />
            </Section>
          )}

          {/* ============ v6.22 登录审计（管理员专属：员工登录 IP/归属地/次数，防借号） ============ */}
          {currentUser?.role === "super_admin" && (
            <Section title="登录审计（管理员）" subtitle="Login Audit · 员工登录 IP·归属地·次数" icon={Shield} color="#F59E0B">
              <LoginAuditPanel />
            </Section>
          )}

          {/* ============ P0：RBAC 权限体系（角色/权限矩阵/用户分配，仅管理员） ============ */}
          {isAdmin && (
            <Section title="权限体系 RBAC" subtitle="Roles · Permissions · 数据范围" icon={Shield} color="#8B5CF6">
              <RBACPanel />
            </Section>
          )}

          {/* ============ P0：审计与合规增强（多维筛选/导出/合规报告，仅管理员） ============ */}
          {isAdmin && (
            <Section title="审计与合规增强" subtitle="Compliance · 导出 · 报告" icon={Shield} color="#10B981">
              <AuditLogEnhanced />
            </Section>
          )}

          {/* ============ P2：安全加固（仅管理员） ============ */}
          {isAdmin && (
            <Section title="安全加固" subtitle="SSO · 2FA · 加密 · 安全扫描" icon={ShieldCheck} color="#10B981">
              <SecurityHardening />
            </Section>
          )}
            </div>
          </div>

          {/* ===== 组 2/5：AI 与赚钱（v6.22 仅管理员：含 API Key 热插拔等敏感配置） ===== */}
          {isAdmin && (
          <div id="sec-ai" className="scroll-mt-2 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-400/90">💰 AI 与赚钱</span>
              <div className="h-px flex-1 bg-white/[0.05]" />
              <span className="text-[9px] font-mono text-gray-600">3 项</span>
            </div>
            <div className="space-y-3">
          {/* ============ AI 自主赚钱 SOP（v6：系统从多维度实施赚钱的操作说明） ============ */}
          <Section title="AI 自主赚钱 SOP" subtitle="AI Money Flywheel · 五个维度实施运行 · 人工协助指南" icon={DollarSign} color="#34D399">
            <AIMoneySOP />
          </Section>

          {/* ============ Token 消耗记录（用户需求 #6：每用户使用次数和 token 消耗） ============ */}
          <Section title="Token 消耗记录" subtitle="Token Usage · 按用户归因 · LLM 调用追踪" icon={Cpu} color="#F59E0B">
            <TokenUsagePanel />
          </Section>

          {/* ============ API Key 热插拔配置 ============ */}
          <Section title="API Key 热插拔配置" subtitle="Hot-Swappable API Keys" icon={KeyRound} color="#F59E0B">
            <div className="space-y-2">
              {apiConfigs.map((config) => (
                <ApiConfigCard
                  key={config.id}
                  config={config}
                  editing={editingConfig === config.id}
                  showKey={showKeys[config.id] || false}
                  testing={testingId === config.id}
                  testResult={testResult[config.id]}
                  onToggleShowKey={() => setShowKeys({ ...showKeys, [config.id]: !showKeys[config.id] })}
                  onEdit={() => setEditingConfig(editingConfig === config.id ? null : config.id)}
                  onSave={(updates) => handleSaveConfig(config.id, updates)}
                  onToggleEnabled={(enabled) => handleToggleEnabled(config.id, enabled)}
                  onTest={config.id === "wechat" ? handleTestWeChat : () => handleTest(config.id)}
                  onReset={() => handleReset(config.id)}
                />
              ))}
            </div>
          </Section>
            </div>
          </div>
          )}

          {/* ===== 组 3/5：大脑与数据（v6.22 仅管理员） ===== */}
          {isAdmin && (
          <div id="sec-brain" className="scroll-mt-2 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-500/90">🧠 大脑与数据</span>
              <div className="h-px flex-1 bg-white/[0.05]" />
              <span className="text-[9px] font-mono text-gray-600">4 项</span>
            </div>
            <div className="space-y-3">
          {/* ============ P0：数据备份与恢复 ============ */}
          <Section title="数据备份与恢复" subtitle="Backup · 定时 · 一键恢复" icon={Archive} color="#06B6D4">
            <BackupPanel />
          </Section>

          {/* ============ 企业大脑（Obsidian）配置 ============ */}
          <Section title="企业大脑热插拔" subtitle="Obsidian Vault · 商业化换脑方案" icon={FolderTree} color="#10B981">
            <EnterpriseBrainPanel />
          </Section>

          {/* ============ P2：多租户与私有化 ============ */}
          <Section title="多租户与私有化" subtitle="Multi-Tenant · 数据隔离 · 私有化部署" icon={Building2} color="#3B82F6">
            <MultiTenantPanel />
          </Section>
          {/* ============ 计划存档查看器 ============ */}
          <Section title="计划存档" subtitle="Plan Archive · 按时间分类" icon={Archive} color="#06B6D4">
            <div className="space-y-3">
              {/* 时间分类 Tab */}
              <div className="flex items-center gap-1">
                {([
                  { id: "daily" as const, label: "日计划", color: "#38BDF8" },
                  { id: "weekly" as const, label: "周计划", color: "#34D399" },
                  { id: "monthly" as const, label: "月计划", color: "#A78BFA" },
                ]).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveArchiveTab(tab.id)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-[11px] font-mono transition-all ${
                      activeArchiveTab === tab.id
                        ? "text-white bg-white/[0.04]"
                        : "text-gray-500 hover:text-gray-300 hover:bg-white/[0.02]"
                    }`}
                    style={activeArchiveTab === tab.id ? { color: tab.color } : {}}
                  >
                    <Calendar size={11} />
                    {tab.label}
                    <span className="text-[9px] text-gray-600 ml-1">
                      {activeArchiveTab === tab.id ? `(${archiveFiles.length})` : ""}
                    </span>
                  </button>
                ))}
                <button
                  onClick={() => loadArchive(activeArchiveTab)}
                  disabled={archiveLoading}
                  className="ml-auto flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-gray-400 hover:text-gray-200 transition-colors"
                >
                  <RefreshCw size={10} className={archiveLoading ? "animate-spin" : ""} />
                  刷新
                </button>
              </div>

              {/* 存档列表 */}
              <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                {archiveLoading ? (
                  <div className="col-span-2 flex items-center justify-center py-8 text-gray-500">
                    <Loader2 size={14} className="animate-spin mr-2" />
                    <span className="text-[11px]">加载中...</span>
                  </div>
                ) : archiveFiles.length === 0 ? (
                  <div className="col-span-2 flex flex-col items-center justify-center py-8 text-gray-600">
                    <Archive size={20} className="mb-2 opacity-50" />
                    <span className="text-[11px]">暂无{activeArchiveTab === "daily" ? "日" : activeArchiveTab === "weekly" ? "周" : "月"}计划存档</span>
                  </div>
                ) : (
                  archiveFiles.map((file, i) => (
                    <button
                      key={i}
                      onClick={() => openArchiveFile(file)}
                      className="text-left rounded-md border border-white/[0.06] bg-white/[0.01] px-3 py-2 hover:border-cyan-500/30 hover:bg-cyan-500/[0.03] transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <FileText size={11} className="text-cyan-400 flex-shrink-0" />
                        <span className="text-[12px] text-gray-200 font-medium truncate">
                          {file.name.replace(/\.md$/i, "")}
                        </span>
                      </div>
                      {file.mtime && (
                        <div className="text-[9px] font-mono text-gray-600">
                          {file.mtime}
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>

              {/* 预览内容 */}
              {previewContent && (
                <div className="rounded-md border border-cyan-500/20 bg-cyan-500/[0.02] p-3 max-h-[400px] overflow-y-auto">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[12px] font-medium text-cyan-300">{previewContent.title}</span>
                    <button
                      onClick={() => setPreviewContent(null)}
                      className="text-[10px] text-gray-500 hover:text-gray-300"
                    >
                      关闭
                    </button>
                  </div>
                  {previewContent.loading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 size={14} className="animate-spin text-cyan-400" />
                    </div>
                  ) : (
                    <pre className="text-[11px] text-gray-300 whitespace-pre-wrap break-words font-mono leading-relaxed">
                      {previewContent.content}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </Section>

            </div>
          </div>
          )}

          {/* ===== 组 4/5：集成与开发者（v6.22 仅管理员） ===== */}
          {isAdmin && (
          <div id="sec-dev" className="scroll-mt-2 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-sky-400/90">🔌 集成与开发者</span>
              <div className="h-px flex-1 bg-white/[0.05]" />
              <span className="text-[9px] font-mono text-gray-600">4 项</span>
            </div>
            <div className="space-y-3">
          {/* ============ P1-功能4：API 开放平台（RESTful API 文档/密钥/应用/版本） ============ */}
          <Section title="API 开放平台" subtitle="P1 · RESTful API · 密钥 · 应用 · 版本" icon={KeyRound} color="#38BDF8">
            <APIPlatformPanel />
          </Section>

          {/* ============ P1-功能5：第三方集成（企业微信/钉钉/飞书/存储/ERP 市场） ============ */}
          <Section title="第三方集成" subtitle="P1 · Integration Market · 热插拔连接" icon={PlugZap} color="#34D399">
            <IntegrationMarketPanel />
          </Section>

          {/* ============ P1-功能6：Webhook 与事件系统（事件订阅/签名/日志） ============ */}
          <Section title="Webhook 与事件系统" subtitle="P1 · Events · HMAC 签名 · 投递日志" icon={Webhook} color="#818CF8">
            <WebhookConsolePanel />
          </Section>


          {/* ============ 环境变量状态 ============ */}
          <Section title="环境变量状态" subtitle="Environment Variables" icon={KeyRound} color="#3B82F6">
            <div className="space-y-1.5">
              {envStatus.map((env) => (
                <div key={env.key} className="flex items-center justify-between py-2 px-3 rounded-md border border-white/[0.04] bg-white/[0.01]">
                  <div className="flex items-center gap-2.5">
                    {env.present ? <CheckCircle2 size={13} className="text-emerald-400" /> : <XCircle size={13} className="text-red-400" />}
                    <div>
                      <div className="text-[12px] text-white font-mono">{env.key}</div>
                      <div className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">{env.label} · {env.source}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${
                    env.present ? "text-emerald-400 bg-emerald-500/10" : "text-red-400 bg-red-500/10"
                  }`}>
                    {env.present ? "已配置" : "未配置"}
                  </span>
                </div>
              ))}
            </div>
          </Section>
            </div>
          </div>
          )}

          {/* ===== 组 5/5：运维与信息（v6.22 仅管理员） ===== */}
          {isAdmin && (
          <div id="sec-ops" className="scroll-mt-2 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-amber-400/90">🖥 运维与信息</span>
              <div className="h-px flex-1 bg-white/[0.05]" />
              <span className="text-[9px] font-mono text-gray-600">3 项</span>
            </div>
            <div className="space-y-3">
          {/* ============ P2：运维监控 ============ */}
          <Section title="运维监控" subtitle="Performance · 告警 · 容量规划" icon={Activity} color="#F59E0B">
            <OpsMonitoring />
          </Section>

          {/* ============ Runtime 收敛治理（52 → 目标 6 层：保留/并入/移出） ============ */}
          <Section title="Runtime 收敛治理" subtitle={`Runtime Governance · ${runtimes.filter((r) => r.ready).length}/${runtimes.length} Ready`} icon={Boxes} color="#A855F7">
            <RuntimeGovernancePanel />
          </Section>

          {/* ============ 关于 ============ */}
          <Section title="关于" subtitle="About" icon={Info} color="#9CA3AF">
            <div className="grid grid-cols-2 gap-2 text-[11px] font-mono">
              <InfoRow label="Version" value="Cockpit 4.0.0" />
              <InfoRow label="Build" value="2026-07-29" />
              <InfoRow label="Phase" value="III · Enterprise Asset Building" />
              <InfoRow label="Storage" value="Obsidian Markdown Vault" />
            </div>
          </Section>
            </div>
          </div>
          )}

        </div>
      </motion.div>
    </div>
  );
}

// ============================================
// 企业大脑（Obsidian）配置子组件
// ============================================

function ObsidianVaultConfig() {
  // 输入框值（默认显示当前 Vault 路径）
  const [vaultRoot, setVaultRoot] = useState(vaultConfig.vaultRoot);
  // 保存后的提示状态
  const [saved, setSaved] = useState(false);

  /**
   * 保存新 Vault 路径：
   * 1. 调用 vaultConfig.update() 触发热插拔
   * 2. 短暂显示"已保存"提示
   * 3. 刷新页面以重新加载所有 Runtime（确保文件系统监听、路径缓存等全部重置）
   */
  const handleSave = () => {
    const trimmed = vaultRoot.trim();
    if (!trimmed) return;
    vaultConfig.update({ vaultRoot: trimmed });
    setSaved(true);
    setTimeout(() => {
      // 刷新页面以重新加载所有 Runtime
      window.location.reload();
    }, 1200);
  };

  return (
    <div className="space-y-2">
      {/* 当前 Vault 路径展示 */}
      <div>
        <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">当前 Vault 路径</label>
        <div className="px-2 py-1 mt-0.5 text-[11px] font-mono text-gray-300 bg-white/[0.02] border border-white/[0.04] rounded break-all">
          {vaultConfig.vaultRoot}
        </div>
      </div>

      {/* 切换 Vault 路径输入框 */}
      <div>
        <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">切换 Vault 路径</label>
        <div className="flex items-center gap-1 mt-0.5">
          <input
            type="text"
            value={vaultRoot}
            onChange={(e) => setVaultRoot(e.target.value)}
            placeholder="D:/Obsidian/小舍AI_中央大脑库"
            className="flex-1 px-2 py-1 text-[11px] font-mono text-white bg-white/[0.03] border border-white/[0.06] rounded focus:outline-none focus:border-emerald-500/40 placeholder-gray-600"
          />
          <button
            onClick={handleSave}
            disabled={saved}
            className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-emerald-600/80 hover:bg-emerald-600 rounded transition-colors disabled:opacity-60"
          >
            <Save size={10} /> {saved ? "已保存" : "保存"}
          </button>
        </div>
      </div>

      {/* 提示信息 */}
      <p className="text-[9px] font-mono text-amber-400/80">
        ⚠️ 切换后将刷新页面以重新加载所有 Runtime
      </p>
      {saved && (
        <p className="text-[10px] font-mono text-emerald-400">✓ 已保存，正在刷新页面...</p>
      )}
    </div>
  );
}

// ============================================
// API 配置卡片子组件
// ============================================

function ApiConfigCard({
  config,
  editing,
  showKey,
  testing,
  testResult,
  onToggleShowKey,
  onEdit,
  onSave,
  onToggleEnabled,
  onTest,
  onReset,
}: {
  config: ApiConfig;
  editing: boolean;
  showKey: boolean;
  testing: boolean;
  testResult?: { success: boolean; message: string; latency?: number };
  onToggleShowKey: () => void;
  onEdit: () => void;
  onSave: (updates: { apiKey?: string; apiBase?: string; extra?: Record<string, string> }) => void;
  onToggleEnabled: (enabled: boolean) => void;
  onTest: () => void;
  onReset: () => void;
}) {
  const [apiKey, setApiKey] = useState(config.apiKey);
  const [apiBase, setApiBase] = useState(config.apiBase);
  const [appId, setAppId] = useState(config.extra?.appId || "");
  const [appSecret, setAppSecret] = useState(config.extra?.appSecret || "");

  useEffect(() => {
    setApiKey(config.apiKey);
    setApiBase(config.apiBase);
    setAppId(config.extra?.appId || "");
    setAppSecret(config.extra?.appSecret || "");
  }, [config]);

  const isWeChat = config.id === "wechat";

  return (
    <div className={`rounded-md border p-3 transition-colors ${
      config.enabled ? "border-white/[0.06] bg-white/[0.01]" : "border-white/[0.04] bg-white/[0.005] opacity-60"
    }`}>
      {/* 头部 */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full ${config.enabled ? "bg-emerald-400" : "bg-gray-500"}`} />
          <span className="text-[12px] font-medium text-white">{config.name}</span>
        </div>
        <div className="flex items-center gap-1">
          {/* 启用/禁用开关 */}
          <button
            onClick={() => onToggleEnabled(!config.enabled)}
            className={`px-2 py-0.5 text-[9px] font-mono rounded transition-colors ${
              config.enabled
                ? "text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20"
                : "text-gray-500 bg-white/[0.03] hover:bg-white/[0.06]"
            }`}
          >
            {config.enabled ? "已启用" : "已禁用"}
          </button>
          <button
            onClick={onEdit}
            className="px-2 py-0.5 text-[9px] font-mono text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 rounded transition-colors"
          >
            {editing ? "取消" : "编辑"}
          </button>
        </div>
      </div>

      {/* 描述 */}
      <p className="text-[10px] text-gray-500 mb-2">{config.description}</p>

      {/* 配置内容 */}
      {editing ? (
        <div className="space-y-2">
          <div>
            <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">API Key</label>
            <div className="flex items-center gap-1">
              <input
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="flex-1 px-2 py-1 text-[11px] font-mono text-white bg-white/[0.03] border border-white/[0.06] rounded focus:outline-none focus:border-amber-500/40"
              />
              <button onClick={onToggleShowKey} className="p-1 text-gray-400 hover:text-gray-200">
                {showKey ? <EyeOff size={12} /> : <Eye size={12} />}
              </button>
            </div>
          </div>
          <div>
            <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">API Base URL</label>
            <input
              type="text"
              value={apiBase}
              onChange={(e) => setApiBase(e.target.value)}
              className="w-full px-2 py-1 text-[11px] font-mono text-white bg-white/[0.03] border border-white/[0.06] rounded focus:outline-none focus:border-amber-500/40"
            />
          </div>
          {isWeChat && (
            <>
              <div>
                <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">AppID</label>
                <input
                  type="text"
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                  placeholder="微信公众号 AppID"
                  className="w-full px-2 py-1 text-[11px] font-mono text-white bg-white/[0.03] border border-white/[0.06] rounded focus:outline-none focus:border-amber-500/40 placeholder-gray-600"
                />
              </div>
              <div>
                <label className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">AppSecret</label>
                <input
                  type="password"
                  value={appSecret}
                  onChange={(e) => setAppSecret(e.target.value)}
                  placeholder="微信公众号 AppSecret"
                  className="w-full px-2 py-1 text-[11px] font-mono text-white bg-white/[0.03] border border-white/[0.06] rounded focus:outline-none focus:border-amber-500/40 placeholder-gray-600"
                />
              </div>
            </>
          )}
          <div className="flex items-center gap-1">
            <button
              onClick={() => onSave({ apiKey, apiBase, extra: isWeChat ? { appId, appSecret } : undefined })}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-white bg-emerald-600/80 hover:bg-emerald-600 rounded transition-colors"
            >
              <Save size={10} /> 保存
            </button>
            <button
              onClick={onReset}
              className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-gray-400 bg-white/[0.03] hover:bg-white/[0.06] rounded transition-colors"
            >
              <RotateCcw size={10} /> 重置
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">API Key</span>
            <span className="text-[10px] font-mono text-gray-300">
              {config.apiKey ? (showKey ? config.apiKey : "••••••••" + config.apiKey.slice(-4)) : "未配置"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">API Base</span>
            <span className="text-[10px] font-mono text-gray-400 truncate ml-2">{config.apiBase}</span>
          </div>
          {isWeChat && (
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">AppID</span>
              <span className="text-[10px] font-mono text-gray-300">
                {config.extra?.appId ? (config.extra.appId.slice(0, 8) + "...") : "未配置"}
              </span>
            </div>
          )}
        </div>
      )}

      {/* 测试按钮和结果 */}
      <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/[0.04]">
        <button
          onClick={onTest}
          disabled={testing}
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-mono text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 rounded transition-colors disabled:opacity-40"
        >
          {testing ? <Loader2 size={10} className="animate-spin" /> : <TestTube size={10} />}
          测试连接
        </button>
        {testResult && (
          <span className={`text-[10px] font-mono ${testResult.success ? "text-emerald-400" : "text-red-400"}`}>
            {testResult.success ? "✓" : "✗"} {testResult.message}
            {testResult.latency ? ` (${testResult.latency}ms)` : ""}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================
// 子组件
// ============================================

function Section({
  title,
  subtitle,
  icon: Icon,
  color,
  children,
}: {
  title: string;
  subtitle: string;
  icon: typeof Settings;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.04]">
        <Icon size={13} style={{ color }} />
        <h3 className="text-[12px] font-medium text-white">{title}</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">{subtitle}</span>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between py-1 px-3 rounded border border-white/[0.04]">
      <span className="text-gray-500 uppercase tracking-wider">{label}</span>
      <span className="text-gray-200">{value}</span>
    </div>
  );
}

// ============================================
// 工具函数
// ============================================

function checkEnv(viteKey: string, processKey: string): boolean {
  try {
    const viteEnv = (import.meta as any).env;
    if (viteEnv && viteEnv[viteKey]) return true;
    if (typeof process !== "undefined" && process.env?.[processKey]) return true;
  } catch (_) {
    // ignore
  }
  return false;
}
